import { Event as GlobalEvent } from "../../Global/Base/Events";
import { Localization } from "../../OW.Remoting/Remoting/Localization";
import { AwaitableEvent } from "../UI.BaseControls/AwaitableEvent";
import { GenericControl } from "../UI.BaseControls/GenericControl";
import { EventHelper } from "../UI.BaseControls/Helpers/EventHelper";
import { IFileUploadEvent, IFileUploadPrevEvent, IFileUploadPrevEventData } from "../UI.BaseControls/IEvent";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { FileEditProgressStatus, FileEditStatus } from "../UI/UI/FileMenu";
import { IFileUploadStatusChangeEvent, IFileUploadStatusChangeEventData } from "./IEvent";

export interface SimpleFileEditElement {
    addEventListener(type: "uploadRequestPreview", listener: (ev: IFileUploadPrevEvent) => any, useCapture?: boolean): void;
    addEventListener(type: "uploadRequest", listener: (ev: IFileUploadEvent) => any, useCapture?: boolean): void;
    addEventListener(type: "statusChange", listener: (ev: IFileUploadStatusChangeEvent) => any, useCapture?: boolean): void;
    addEventListener(type: "browseDialogOpened", listener: (ev: CustomEvent) => any, useCapture?: boolean): void;
    addEventListener(type: string, listener: EventListenerOrEventListenerObject, useCapture?: boolean): void;

    registerEventListener(type: "uploadRequestPreview", listener: (ev: IFileUploadPrevEvent) => any, useCapture?: boolean): void;
    registerEventListener(type: "uploadRequest", listener: (ev: IFileUploadEvent) => any, useCapture?: boolean): void;
    registerEventListener(type: "statusChange", listener: (ev: IFileUploadStatusChangeEvent) => any, useCapture?: boolean): void;
    registerEventListener(type: "browseDialogOpened", listener: (ev: CustomEvent) => any, useCapture?: boolean): void;
    registerEventListener(type: string, listener: EventListenerOrEventListenerObject, useCapture?: boolean): void;
}

/**
 * Tlačítko se souborem bez menu
 */
export class SimpleFileEditElement extends HTMLElement {
    public readonly downloadFileClicked: GlobalEvent<SimpleFileEditElement, void> = new GlobalEvent<SimpleFileEditElement, void>();

    private _fileName: string;
    private _status: FileEditStatus;
    private _isLocked: boolean = false;
    protected _innerContent: HTMLDivElement;
    protected _fileElement: HTMLInputElement;
    private _contentElement: HTMLElement;
    private _textElement: HTMLElement;
    private _progressElement: HTMLElement;
    protected _failBtnBar: HTMLDivElement;
    protected _failReloadBtn: HTMLButtonElement;
    private _dropZoneElement: HTMLElement;
    private _dropzoneListener: any;
    protected _files: File[];
    //#region Properties

    /**
    * Používá se k provázání caption s elementem
    */
    public get textElement(): HTMLElement {
        return this._textElement;
    }

    public set visible(v: boolean) {
        this.classList.toggle("Hidden", !v);
    }

    public get visible(): boolean {
        return !this.classList.contains("Hidden");
    }

    /**
     * Název souboru
     */
    public set fileName(v: string) {
        if (this.isLocked)
            return;

        const changed = this._fileName !== v;
        this._fileName = v;

        this.setStatus(v ? FileEditStatus.download : FileEditStatus.empty);

        // vždy, když se změnil název souboru, tak je potřeba název nového souboru promítnout
        if (changed)
            this._updateText();
    }

    public get fileName(): string {
        return this._fileName;
    }

    /**
     * Určuje, zda je komponenta uzamčená pro změnu názvu souboru
     */
    public get isLocked(): boolean {
        return this._isLocked;
    }

    public set isLocked(uploading: boolean) {
        this._isLocked = uploading;
    }

    public get status(): FileEditStatus {
        return this._status;
    }

    public set mask(v: string) {
        this._fileElement.accept = v;
    }

    public get mask(): string {
        return this._fileElement.accept;
    }

    public set value(v: string) {
        this.fileName = v;
    }

    public get value(): string {
        return this._fileName;
    }

    public set enabled(v: boolean) {
        this._fileElement.disabled = !v;
        this.classList.toggle("disabled", !v);
    }

    public get enabled(): boolean {
        return !this._fileElement.disabled;
    }

    public set dropzone(v: HTMLElement) {
        if (this._dropZoneElement)
            this._dropZoneUnregister();
        this._dropZoneElement = v;
        this._dropZoneRegister(v);
    }

    public get dropzone(): HTMLElement {
        return this._dropZoneElement;
    }

    protected static get observedAttributes(): string[] {
        return ["filemask"];
    }
    //#endregion Properties

    /**
     * Spustí dialog pro otevření okna
     */
    protected _triggerOpenFile(): void {
        this._fileElement.click();
        this.callEvent("browseDialogOpened");
    }

    /*public fillSignatureInfoMenu(signatureInfoMenu: DropDownMenu, event: MouseEvent) {
        this._signatureInfoMenu = signatureInfoMenu;
        this._signatureInfoMenu.Show(this as HTMLElement, event);
    }*/

    public changeProgressStatus(type: FileEditProgressStatus, value?: string | number): void {
        this.removeAttribute("title");
        if (type === FileEditProgressStatus.progressValue) {
            this.setStatus(FileEditStatus.uploading);
            this._progressElement.style.width = value + "%";
        } else if (type === FileEditProgressStatus.fail) {
            this.setStatus(FileEditStatus.error);
        } else if (type === FileEditProgressStatus.complete) {
            this.classList.add("finishing", "uploading");
            this._progressElement.style.width = "100%";
        } else if (type === FileEditProgressStatus.cancel) {
            this.setStatus(FileEditStatus.empty);
        }
    }

    public setStatus(status: FileEditStatus): boolean {
        if (this._status === status)
            return false;

        if (this._status === FileEditStatus.uploading)
            this._progressElement.style.width = "0";

        this.callEvent("statusChange", { status: status } as IFileUploadStatusChangeEventData);
        this._status = status;
        this.classList.remove("error", "uploading", "empty", "download", "finishing", "unavailable");
        this.classList.add(FileEditStatus[status].toString());

        this._updateText();
        return true;
    }

    protected _updateText(): void {
        if (this._status === FileEditStatus.empty) {
            this._textElement.innerHTML = "<span class='Icon'></span>";
            const emptyElement = document.createElement("span");
            Localization.addLocData(emptyElement, "GUI-600106");
            this._textElement.appendChild(emptyElement);
        } else {
            this._textElement.textContent = this.fileName;
        }
    }

    /**
     * Zpracování souborů
     * @param files Seznam souborů
     */
    protected async _handleFiles(files: FileList): Promise<boolean> {
        if (files.length === 0)
            return false;

        // v tuto chvíli se zpracovává vždy pouze první soubor
        const file = files[0];

        // událost umožňuje zrušit upload
        const detail: IFileUploadPrevEventData = { files: [file] };
        const eventData = new AwaitableEvent("uploadRequestPreview", true, { detail });
        this.dispatchEvent(eventData);
        if (!await eventData.resultAwaiter)
            return false;

        this._files = [file];
        this.fileName = file.name;

        this.setStatus(FileEditStatus.uploading);
        return true;
    }

    private _dropZoneRegister(element: HTMLElement): void {
        if (!element)
            return;

        element.addEventListener("dragenter", this._dropzoneListener["dragenter"] = (e: DragEvent) => {
            this.classList.add("DragIn");
            e.preventDefault();
        });
        element.addEventListener("dragover", this._dropzoneListener["dragover"] = (e: DragEvent) => {
            //this.classList.remove("DragIn");
            e.preventDefault();
        });
        element.addEventListener("dragleave", this._dropzoneListener["dragleave"] = (e: DragEvent) => {
            this.classList.remove("DragIn");
            e.preventDefault();
        });
        element.addEventListener("drop", this._dropzoneListener["drop"] = async (e: DragEvent) => {
            e.preventDefault();
            if (this.status === FileEditStatus.uploading || !this.enabled)
                return;

            const dt = e.dataTransfer;
            if (!(await this._handleFiles(dt.files)))
                return;
            this.callEvent("uploadRequest", { files: this._files });
        });
        this._dropZoneElement = element;
    }

    private _dropZoneUnregister(): void {
        this._dropZoneElement.removeEventListener("dragenter", this._dropzoneListener["dragenter"]);
        this._dropZoneElement.removeEventListener("dragleave", this._dropzoneListener["dragleave"]);
        this._dropZoneElement.removeEventListener("dragover", this._dropzoneListener["dragover"]);
        this._dropZoneElement.removeEventListener("drop", this._dropzoneListener["drop"]);
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(setStatusAndEnabled: boolean = true): void {
        GenericControl._constructor(this as HTMLElement);
        this._dropzoneListener = {};
        this.classList.add("SimpleFileEditElement");
        this._files = [];
        this._fileElement = createElement({ type: "input[file]" }) as HTMLInputElement;
        this._contentElement = createElement({ type: "div", classes: "Content" }) as HTMLElement;
        this._textElement = createElement({ type: "div", classes: "Text", value: "" }) as HTMLElement;
        this._progressElement = createElement({ type: "div", classes: "ProgressBar" }) as HTMLElement;
        this._failReloadBtn = createElement({ type: "button", classes: "repeat" }) as HTMLButtonElement;
        this._innerContent = createElement({ type: "div", classes: "InnerContent" }) as HTMLDivElement;
        this._failBtnBar = createElement({ type: "div", classes: "ButtonsContent" }) as HTMLDivElement;
        this.appendChild(this._fileElement);
        this._failBtnBar.appendChild(this._failReloadBtn);
        this._innerContent.appendChild(this._textElement);
        this._innerContent.appendChild(this._failBtnBar);
        this._contentElement.appendChild(this._progressElement);
        this._contentElement.appendChild(this._innerContent);
        this.appendChild(this._contentElement);

        if (setStatusAndEnabled) {
            this.setStatus(FileEditStatus.empty);
            this.enabled = true;
        }
    }

    protected connectedCallback(): void {
        this.registerEventListener("dragenter", (e: DragEvent) => {
            this.classList.add("DragIn");
            e.preventDefault();
        });
        this.registerEventListener("dragover", (e: DragEvent) => {
            //this.classList.remove("DragIn");
            e.preventDefault();
        });
        this.registerEventListener("dragleave", (e: DragEvent) => {
            this.classList.remove("DragIn");
            e.preventDefault();
        });
        this.registerEventListener("drop", async (e: DragEvent) => {
            e.preventDefault();
            if (this.status === FileEditStatus.uploading || !this.enabled)
                return;

            const dt = e.dataTransfer;
            if (!(await this._handleFiles(dt.files)))
                return;
            this.callEvent("uploadRequest", { files: this._files });
        });
        GenericControl.registerEventListener(this._fileElement, "click", (e: MouseEvent) => {
            EventHelper.stopPropagation(e);
            this._fileElement.value = "";
        });

        this.connectedCallbackSpecial();
    }

    protected connectedCallbackSpecial(): void {
        this.registerEventListener("click", (e: MouseEvent) => {
            EventHelper.stopPropagation(e);
            if (this.status === FileEditStatus.download || this.status === FileEditStatus.unavailable) {
                this.downloadFileClicked.trigger(this, null);
            } else if (this.status === FileEditStatus.empty) {
                if (!this.enabled)
                    return;
                this._triggerOpenFile();
            }
        });
        GenericControl.registerEventListener(this._failReloadBtn, "click", (e: MouseEvent) => {
            EventHelper.stopPropagation(e);
            this.changeProgressStatus(FileEditProgressStatus.progressValue, 0);
            this.callEvent("uploadRequest", { files: this._files, retry: true });
        });
        GenericControl.registerEventListener(this._fileElement, "change", async (e) => {
            e.stopPropagation();
            if (!(await this._handleFiles(this._fileElement.files)))
                return;
            this.callEvent("uploadRequest", { files: this._files });
        });
    }

    protected disconnectedCallback(): void {
        this.unregisterEventListener();
        GenericControl.unregisterEventListener(this._failReloadBtn);
        GenericControl.unregisterEventListener(this._fileElement);
    }

    protected attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        GenericControl.attributeChangedCallback(this as HTMLElement, attrName, oldVal, newVal);

        if (attrName == "filemask" && newVal) {
            this._fileElement.accept = newVal;
        }
    }

    //#region Zpracování metod z globální třídy GenericControl
    /**
     * Zaregistruje událost
     * @param event Název události
     * @param action Akce při vyvolání události
     */
    public registerEventListener(event: string, action: (e: CustomEvent) => any, _useCapture?: boolean): void {
        GenericControl.registerEventListener(this as HTMLElement, event, action);
    }

    /**
     * Odregistruje udalost. Pokud neni vyplnen zadny parametr, jsou odregistrovany vsechny udalosti. Pokud je vyplnen pouze event, odregistruje vsechny udalosti daneho typu
     * pokud je vyplnen event a action, odregistruje pouze konkretni akci
     * @param event Event, ktery chcete odregistrovat.
     * @param action Akce, ktere se tyka event a chcete ji odregistrovat
     */
    public unregisterEventListener(event?: string, action?: (e: Event) => void): void {
        GenericControl.unregisterEventListener(this as HTMLElement, event, action);
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    public callEvent(eventName: string, detail?: any, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        return GenericControl.callEvent(this as HTMLElement, eventName, detail, bubbles, cancelable);
    }

    /**
     * Vrátí pozici elementu v rámci dokumentu
     */
    public offset(): { top: number; left: number } {
        return GenericControl.offset(this as HTMLElement);
    }

    //#enregion Zpracování metod z globální třídy GenericControl
}

registerCustomElement("tsc-SimpleFileEdit", SimpleFileEditElement);
